/**
 * Handwritten TanStack Query hooks wrapping the generated SDK.
 *
 * Pattern:
 *   - One query/mutation hook per endpoint.
 *   - `queryKey` is the hook's stable identifier. Mutations invalidate the
 *     keys whose data they make stale.
 *   - All hooks return the standard TanStack Query result, so consumers can
 *     destructure `{ data, isLoading, error, mutate, ... }` as usual.
 *
 * To add a hook for a new endpoint, follow the existing examples — copy a
 * useQuery wrapper for a GET, a useMutation wrapper for a POST/PUT/DELETE,
 * and call `queryClient.invalidateQueries` for any keys the mutation
 * affects. Never import the generated SDK directly from feature code —
 * import these hooks (or the SDK re-exports from `@/lib/api`) instead.
 */

import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { isApiError } from "@/lib/api/client";
import {
  addGroupMember,
  addProjectMember,
  adminActivityActions,
  adminActivityAnalytics,
  adminApprovePromotionRequest,
  adminApproveServiceBudgetRequest,
  adminDeleteProject,
  adminDeleteUser,
  adminGetAiServices,
  adminGetUser,
  adminInviteUser,
  adminListActivity,
  adminListPromotionRequests,
  adminListServiceBudgetRequests,
  adminListUsers,
  adminRefreshProjects,
  adminReissueServiceKeys,
  adminRejectPromotionRequest,
  adminRejectServiceBudgetRequest,
  adminRevokeServiceKey,
  adminRotateServiceKey,
  adminRunDeployIdentityScopeMigration,
  adminSetLlmMode,
  adminSetProjectSuspension,
  adminSetServiceBudget,
  adminSetServiceKillSwitch,
  adminSetServiceSuspension,
  adminSetServiceTier,
  adminUpdateUser,
  type AppRegistrationRequestDetail,
  approveAppRegistration,
  checkSignInApproval,
  type CreateAppRegistrationAmendment,
  createAppRegistrationAmendment,
  type CreateAppRegistrationRequest,
  createAppRegistrationRequest,
  createGroup,
  deleteGroup,
  deleteProject,
  type DeployIdentityScopeMigrationRequest,
  getActivity,
  getAppRegistrationAmendmentBase,
  getHealth,
  getMe,
  getMeDetail,
  getProjectResourcesStatus,
  getProjectServices,
  getProjectSignIn,
  getVersion,
  githubConnect,
  githubDisconnect,
  githubStatus,
  inviteUser,
  type LangfuseLinkRequest,
  linkLangfuseProject,
  listAppRegistrationEnvironments,
  listAppRegistrationScopes,
  listGroupMembers,
  listGroups,
  listMyGroups,
  listPendingAppRegistrationRequests,
  listProjectAppRegistrationRequests,
  listProjectEnvironments,
  listProjectMembers,
  listProjects,
  listSignInApprovals,
  logout,
  markSignInApproved,
  migrateDeployIdentity,
  type ProjectSummary,
  promoteProject,
  type PromoteProjectRequest,
  reconcileAppRegistrations,
  redeployProjectEnvironment,
  type RedeployResult,
  rejectAppRegistration,
  removeGroupMember,
  removeProjectMember,
  requestAppRegistrationDecommission,
  requestServiceBudget,
  searchUsers,
  sendTestNotification,
  type ServiceUsage,
  type SetServiceBudget,
  updateGroup,
  updateProfile,
  updateProject,
} from "@/lib/api/generated";
import type { ResourceStatusMap } from "@/lib/local-types";
import { isAwaitingDeployUrl } from "@/lib/project-status";

// ─── Query keys ────────────────────────────────────────────────────────────

/** Filter/sort/pagination params for the admin activity feed. */
export interface AdminActivityParams {
  search?: string;
  action?: string;
  userId?: string;
  sortBy?: string;
  sortDir?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

/** Server-side sort selection for the users/projects list views. */
export interface TableSortParams {
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export const queryKeys = {
  health: ["health"] as const,
  version: ["version"] as const,
  me: ["me"] as const,
  meDetail: ["me", "detail"] as const,
  activity: ["activity"] as const,
  projects: ["projects"] as const,
  projectMembers: (projectId: number) => ["projects", projectId, "members"] as const,
  projectResourceStatus: (projectId: number) => ["projects", projectId, "resources"] as const,
  projectEnvironments: (projectId: number) => ["projects", projectId, "environments"] as const,
  appRegistrationAmendmentBase: (requestId: string) =>
    ["app-registration-requests", requestId, "amendment"] as const,
  projectAppRegistrationRequests: (projectId: number) =>
    ["projects", projectId, "app-registration-requests"] as const,
  githubStatus: ["github", "status"] as const,
  adminUsers: ["admin", "users"] as const,
  adminUser: (userId: string | null) => ["admin", "users", userId] as const,
  adminActivity: (params: AdminActivityParams) => ["admin", "activity", params] as const,
  adminActivityAnalytics: (days: number) => ["admin", "activity", "analytics", days] as const,
  adminActivityActions: ["admin", "activity", "actions"] as const,
  userSearch: (query: string, exclude: string) => ["users", "search", query, exclude] as const,
  groups: ["groups"] as const,
  groupMembers: (groupId: number) => ["groups", groupId, "members"] as const,
  myGroups: ["groups", "mine"] as const,
  adminAppRegistrationRequests: ["admin", "app-registration-requests"] as const,
  appRegistrationReconciliation: ["admin", "app-registration-reconciliation"] as const,
  appRegistrationScopes: ["app-registration-scopes"] as const,
  appRegistrationEnvironments: (projectId: number) =>
    ["projects", projectId, "app-registration-environments"] as const,
  projectSignIn: (projectId: number) => ["projects", projectId, "sign-in"] as const,
  /** Prefix of every `signInApprovals` key, for invalidating both filter states at once. */
  signInApprovalsAll: ["admin", "sign-in-approvals"] as const,
  signInApprovals: (includeConsented: boolean) =>
    ["admin", "sign-in-approvals", { includeConsented }] as const,
  adminPromotionRequests: ["admin", "promotion-requests"] as const,
  projectServices: (projectId: number) => ["projects", projectId, "services"] as const,
  adminServiceBudgetRequests: ["admin", "service-budget-requests"] as const,
  adminAiServices: ["admin", "ai-services"] as const,
};

// ─── System ──────────────────────────────────────────────────────────────

export function useHealth(enabled = true, refetchInterval?: number) {
  return useQuery({
    queryKey: queryKeys.health,
    queryFn: async () => {
      const { data } = await getHealth();
      return data ?? null;
    },
    enabled,
    refetchInterval,
  });
}

export function useVersion(enabled = true) {
  return useQuery({
    queryKey: queryKeys.version,
    queryFn: async () => {
      const { data } = await getVersion();
      return data ?? null;
    },
    enabled,
  });
}

// ─── Profile / auth ──────────────────────────────────────────────────────

export function useMe() {
  return useQuery({
    queryKey: queryKeys.me,
    queryFn: async () => {
      const { data } = await getMe();
      return data ?? null;
    },
    // Don't retry on a 401 — ProtectedRoute redirects to /login.
    retry: false,
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await logout();
    },
    onSuccess: () => {
      // Clear the entire cache so no stale per-user data leaks across sessions.
      queryClient.clear();
    },
  });
}

export function useMeDetail(enabled = true) {
  return useQuery({
    queryKey: queryKeys.meDetail,
    queryFn: async () => {
      const { data } = await getMeDetail();
      return data ?? null;
    },
    enabled,
    retry: false,
  });
}

export function useActivity() {
  return useQuery({
    queryKey: queryKeys.activity,
    queryFn: async () => {
      const { data } = await getActivity();
      return data ?? [];
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { firstName: string | null; lastName: string | null }) => {
      await updateProfile({
        body: { first_name: vars.firstName, last_name: vars.lastName },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.me });
      void queryClient.invalidateQueries({ queryKey: queryKeys.meDetail });
    },
  });
}

// ─── Projects ──────────────────────────────────────────────────────────────

// A project flips to "completed" when the scaffold finishes, but its deployed
// app_url only lands minutes later via the deploy callback (NOR-292). Poll the
// list while any completed project is still missing its URL (see
// `isAwaitingDeployUrl`) so the "Open app" link appears on its own.
const APP_URL_POLL_INTERVAL_MS = 15000;

export function useProjects(enabled = true, sort: TableSortParams = {}) {
  return useQuery({
    // Unsorted callers (the home view) keep the bare ["projects"] key so their
    // optimistic cache writes and invalidations still match; a sort narrows it.
    queryKey: sort.sortBy ? [...queryKeys.projects, sort] : queryKeys.projects,
    queryFn: async () => {
      const { data } = await listProjects(
        sort.sortBy ? { query: { sort_by: sort.sortBy, sort_dir: sort.sortDir ?? "asc" } } : {},
      );
      return data ?? [];
    },
    enabled,
    placeholderData: (prev) => prev,
    refetchInterval: (query) => {
      const projects = query.state.data;
      if (!projects?.length) {
        return false;
      }
      return projects.some(isAwaitingDeployUrl) ? APP_URL_POLL_INTERVAL_MS : false;
    },
  });
}

export function useProjectMembers(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey:
      projectId === null ? ["projects", "members", "none"] : queryKeys.projectMembers(projectId),
    queryFn: async () => {
      if (projectId === null) {
        return [];
      }
      const { data } = await listProjectMembers({ path: { project_id: projectId } });
      return data ?? [];
    },
    enabled: enabled && projectId !== null,
  });
}

export function useProjectResourceStatus(projectId: number | null) {
  return useQuery({
    queryKey:
      projectId === null
        ? ["projects", "resources", "none"]
        : queryKeys.projectResourceStatus(projectId),
    queryFn: async () => {
      if (projectId === null) {
        return null;
      }
      const { data } = await getProjectResourcesStatus({ path: { project_id: projectId } });
      return (data ?? null) as ResourceStatusMap | null;
    },
    enabled: projectId !== null,
  });
}

export function useAddProjectMember(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      await addProjectMember({
        path: { project_id: projectId },
        body: { email: vars.email },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectMembers(projectId) });
    },
  });
}

export function useRemoveProjectMember(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { memberUserId: string }) => {
      await removeProjectMember({
        path: { project_id: projectId, member_user_id: vars.memberUserId },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectMembers(projectId) });
    },
  });
}

/** A project's promoted `test`/`prod` environment rows (no row for `dev`, see `ProjectEnvironment`). */
export function useProjectEnvironments(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey:
      projectId === null
        ? ["projects", "environments", "none"]
        : queryKeys.projectEnvironments(projectId),
    queryFn: async () => {
      if (projectId === null) {
        return [];
      }
      const { data } = await listProjectEnvironments({ path: { project_id: projectId } });
      return data ?? [];
    },
    enabled: enabled && projectId !== null,
  });
}

/**
 * Mutation key of `usePromoteProject`, scoped per project so siblings of the
 * promote controls (see `LangfuseSection`) can tell a promotion is in flight
 * -- `useMutation` state is per-hook-instance and invisible across components.
 */
export const promoteProjectMutationKey = (projectId: number) =>
  ["promoteProject", projectId] as const;

/**
 * Promote a project to `test` (off `dev`) or `prod` (off `test`) -- admin only.
 *
 * Invalidation runs in `onSettled` rather than `onSuccess`: a 502 means the
 * essential steps failed but the `project_environments` row was still
 * persisted (see `services/promote.py`), so the caller needs a refetch on
 * failure too, not just on success.
 */
export function usePromoteProject(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: promoteProjectMutationKey(projectId),
    mutationFn: async (vars: {
      target: PromoteProjectRequest["target"];
      sentry_level?: PromoteProjectRequest["sentry_level"];
    }) => {
      const { data } = await promoteProject({
        path: { project_id: projectId },
        body: { target: vars.target, sentry_level: vars.sentry_level },
      });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectEnvironments(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Link a project to its own dedicated Langfuse project (NOR-477) -- admin
 * only. Invalidates `projects` on success so the linked name/timestamp and
 * the promote pipeline's badges refresh together.
 */
export function useLinkLangfuseProject(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: LangfuseLinkRequest) => {
      const { data } = await linkLangfuseProject({
        path: { project_id: projectId },
        body: vars,
      });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Move a legacy project off the shared instance deploy identity onto its own
 * (NOR-471) -- admin only.
 *
 * Invalidation runs in `onSettled` rather than `onSuccess` so a failed attempt
 * refetches too: the whole endpoint is idempotent and re-runnable, so the
 * project list should reflect whatever state it actually reached rather than
 * the one the UI assumed.
 */
export function useMigrateDeployIdentity(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await migrateDeployIdentity({ path: { project_id: projectId } });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Dry-run (default), migrate or roll back one project's deploy identity scope --
 * admin only. A run that is not a dry run changes the project's Azure access and
 * is confirmed with its repository name; the report's `state` says what happened.
 */
export function useRunDeployIdentityScopeMigration(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (options: {
      dryRun: boolean;
      force: boolean;
      adopt: NonNullable<DeployIdentityScopeMigrationRequest["adopt"]>;
      rollback: boolean;
      confirm?: string;
    }) => {
      const { data } = await adminRunDeployIdentityScopeMigration({
        body: {
          project_id: projectId,
          dry_run: options.dryRun,
          force: options.force,
          adopt: options.adopt,
          rollback: options.rollback,
          confirm: options.confirm ?? null,
        },
      });
      return data ?? null;
    },
    onSettled: (_data, _error, options) => {
      if (!options.dryRun) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
      }
    },
  });
}

/**
 * Redeploy one of a project's environments from its branch -- owner or admin.
 * For a project whose resource groups ScaffoldUI creates, the backend first
 * re-creates the environment's resource group and its deploy identity's access
 * (the way back after an Azure-only teardown), then starts the deploy workflow.
 */
export function useRedeployProjectEnvironment(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (environment: RedeployResult["environment"]) => {
      const { data } = await redeployProjectEnvironment({
        path: { project_id: projectId, environment },
      });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectResourceStatus(projectId) });
    },
  });
}

/** Reassign (or clear) a project's group after creation -- owner or admin only. */
export function useUpdateProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { projectId: number; groupId: number | null }) => {
      const { data } = await updateProject({
        path: { project_id: vars.projectId },
        body: { group_id: vars.groupId },
      });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

// ─── App registration requests ─────────────────────────────────────────────

/**
 * The delegated permissions a request may ask for, in picker order.
 *
 * The server is the only source of truth for this catalogue: the risk tier and
 * `admin_consent_required` flag a requester picks by and an approver decides
 * on are the same values provisioning acts on, so nothing here second-guesses
 * or re-sorts them.
 *
 * Effectively static per deploy — it only changes when the backend catalogue
 * does — so it is cached for an hour instead of being refetched by every table
 * row and dialog that needs to resolve a scope key.
 */
export function useAppRegistrationScopes(enabled = true) {
  return useQuery({
    queryKey: queryKeys.appRegistrationScopes,
    queryFn: async () => {
      const { data } = await listAppRegistrationScopes();
      return data?.scopes ?? [];
    },
    enabled,
    staleTime: 60 * 60 * 1000,
    // Outlive the 5-minute default so navigating away and back doesn't refetch.
    gcTime: 60 * 60 * 1000,
  });
}

/**
 * Which of a project's environments can take an app registration.
 *
 * Not cached like the scope catalogue: this changes whenever the project is
 * promoted or another request is raised, and an intake form offering an
 * environment that has just been taken submits straight into a 409.
 */
export function useAppRegistrationEnvironments(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.appRegistrationEnvironments(projectId),
    queryFn: async () => {
      const { data } = await listAppRegistrationEnvironments({ path: { project_id: projectId } });
      return data ?? { available: [], taken: [], sign_in: [] };
    },
    enabled,
  });
}

/**
 * A project's app registration requests, newest first (owner or admin only).
 *
 * The same `AppRegistrationRequestDetail` shape the approver queue renders, so
 * a request looks identical on both sides of the desk.
 */
export function useProjectAppRegistrationRequests(projectId: number | null, enabled = true) {
  return useQuery({
    queryKey:
      projectId === null
        ? ["projects", "app-registration-requests", "none"]
        : queryKeys.projectAppRegistrationRequests(projectId),
    queryFn: async (): Promise<AppRegistrationRequestDetail[]> => {
      if (projectId === null) {
        return [];
      }
      const { data } = await listProjectAppRegistrationRequests({
        path: { project_id: projectId },
      });
      return data ?? [];
    },
    enabled: enabled && projectId !== null,
  });
}

/**
 * Request an app registration for a project.
 *
 * `display_name` and `requester_email` are deliberately absent from the body:
 * the backend derives both (from the project row and the session), and ignores
 * anything a client sends for them.
 *
 * Callers must handle a **409** — the server runs managed-identity triage and
 * refuses the premise rather than the payload. That is a verdict to explain,
 * not an error to toast; see the generated `AppRegistrationTriageRejection`
 * type.
 */
export function useCreateAppRegistrationRequest(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateAppRegistrationRequest) => {
      const { data } = await createAppRegistrationRequest({
        path: { project_id: projectId },
        body: vars,
      });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectAppRegistrationRequests(projectId),
      });
    },
  });
}

/**
 * What an amendment to this registration would start from.
 *
 * Never cached beyond the dialog that opened it. The value it returns is
 * echoed back as `base_scope_keys` and the server refuses a mismatch, so a
 * stale one does not corrupt anything — it just spends a round trip and a
 * requester's edit on a 409 they cannot do anything about except reopen the
 * form. Refetching on mount is cheaper than that.
 */
export function useAppRegistrationAmendmentBase(requestId: string | null) {
  return useQuery({
    queryKey: queryKeys.appRegistrationAmendmentBase(requestId ?? "none"),
    queryFn: async () => {
      if (requestId === null) {
        return null;
      }
      const { data } = await getAppRegistrationAmendmentBase({
        path: { request_id: requestId },
      });
      return data ?? null;
    },
    enabled: requestId !== null,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * Ask to change an issued registration's delegated permissions.
 *
 * Callers must handle a **409**: the registration may have been amended,
 * rejected or decommissioned since the form was opened, and the server refuses
 * rather than letting one amendment silently revert another. That is a state
 * to re-read, not an error to retry.
 */
export function useCreateAppRegistrationAmendment(requestId: string, projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: CreateAppRegistrationAmendment) => {
      const { data } = await createAppRegistrationAmendment({
        path: { request_id: requestId },
        body: vars,
      });
      return data ?? null;
    },
    onSuccess: () => {
      // Both lists and the base: the new row belongs in the project's list and
      // in the approver queue, and the registration can no longer take another
      // amendment until this one is decided.
      invalidateAppRegistrationRequest(queryClient, projectId);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.appRegistrationAmendmentBase(requestId),
      });
    },
  });
}

// ─── GitHub / platform access ──────────────────────────────────────────────

export function useGithubStatus() {
  return useQuery({
    queryKey: queryKeys.githubStatus,
    queryFn: async () => {
      const { data } = await githubStatus();
      return data ?? null;
    },
    // The backend live-checks org membership on every call, so a freshly
    // granted membership should surface without a manual reload. Refetch on
    // focus, and while connected-but-not-yet-verified poll every 30s; stop
    // once org access is confirmed (or the account is disconnected).
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const status = query.state.data;
      // No cached status yet (e.g. the initial fetch failed) — keep polling
      // so the banner can recover without waiting for a focus event.
      if (!status) {
        return 30_000;
      }
      return status.connected && status.has_org_access !== true ? 30_000 : false;
    },
  });
}

export function useDisconnectGithub() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await githubDisconnect();
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.githubStatus });
    },
  });
}

export function useGithubConnectUrl() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await githubConnect();
      return data ?? null;
    },
  });
}

// ─── User search / invite ──────────────────────────────────────────────────

export function useUserSearch(query: string, exclude: string[]) {
  const trimmed = query.trim();
  const excludeStr = exclude.join(",");
  return useQuery({
    queryKey: queryKeys.userSearch(trimmed, excludeStr),
    queryFn: async () => {
      const { data } = await searchUsers({
        query: { q: trimmed, ...(excludeStr ? { exclude: excludeStr } : {}) },
      });
      return data ?? [];
    },
    enabled: trimmed.length >= 2,
    placeholderData: (prev) => prev,
  });
}

export function useInviteColleague() {
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await inviteUser({ body: { email: vars.email } });
      return data ?? null;
    },
  });
}

// ─── Admin ───────────────────────────────────────────────────────────────

export function useAdminUsers(sort: TableSortParams = {}) {
  return useQuery({
    // A sort narrows the key; unsorted keeps the bare ["admin","users"] key so
    // mutation invalidations by queryKeys.adminUsers still match.
    queryKey: sort.sortBy ? [...queryKeys.adminUsers, sort] : queryKeys.adminUsers,
    queryFn: async () => {
      const { data } = await adminListUsers(
        sort.sortBy ? { query: { sort_by: sort.sortBy, sort_dir: sort.sortDir ?? "asc" } } : {},
      );
      return data ?? [];
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminUser(userId: string | null, enabled = true) {
  return useQuery({
    queryKey: queryKeys.adminUser(userId),
    queryFn: async () => {
      if (!userId) {
        return null;
      }
      const { data } = await adminGetUser({ path: { user_id: userId } });
      return data ?? null;
    },
    enabled: enabled && !!userId,
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      userId: string;
      firstName: string | null;
      lastName: string | null;
      role: string;
    }) => {
      await adminUpdateUser({
        path: { user_id: vars.userId },
        body: { first_name: vars.firstName, last_name: vars.lastName, role: vars.role },
      });
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUser(vars.userId) });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await adminDeleteUser({ path: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

export function useInviteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { email: string }) => {
      const { data } = await adminInviteUser({ body: { email: vars.email } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminUsers });
    },
  });
}

// Removing a project tears down what is left of it (Azure resource groups,
// deploy credential, Entra redirect URI) before the backend answers, which
// regularly outlasts the ingress timeout in front of it. The request then
// fails on the client while the backend carries on and finishes, so a failed
// request is not a failed removal: poll the list until the project is gone
// before believing the error.
const REMOVAL_POLL_INTERVAL_MS = 5000;
const REMOVAL_POLL_MAX_ATTEMPTS = 24; // ~2 min

async function projectGoneAfterPolling(projectId: number): Promise<boolean> {
  for (let attempt = 0; attempt < REMOVAL_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, REMOVAL_POLL_INTERVAL_MS));
    try {
      const { data } = await listProjects();
      if (data && !data.some((p) => p.id === projectId)) {
        return true;
      }
    } catch (err) {
      // A 4xx here (expired session, forbidden) means the poll can never
      // confirm anything: give up now. Transport and 5xx failures are
      // transient; keep polling until the deadline.
      if (isApiError(err) && err.status >= 400 && err.status < 500) {
        throw err;
      }
    }
  }
  return false;
}

// Only failures that leave the outcome unknown are worth waiting on: no HTTP
// response at all (network error, aborted connection) or a 5xx such as the
// ingress 504. A 4xx is the backend refusing the request, so nothing is
// being torn down and the caller should hear about it right away.
function isAmbiguousFailure(err: unknown): boolean {
  return !isApiError(err) || err.status === 0 || err.status >= 500;
}

async function removeProjectTolerantly(
  projectId: number,
  request: () => Promise<{ success?: boolean; error?: string | null } | undefined>,
) {
  let result: Awaited<ReturnType<typeof request>>;
  try {
    result = await request();
  } catch (err) {
    if (isAmbiguousFailure(err) && (await projectGoneAfterPolling(projectId))) {
      return;
    }
    throw err;
  }
  if (result && result.success === false) {
    throw new Error(result.error || "Failed to remove project");
  }
}

export function useDeleteAdminProject() {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * *appRegistrationsReason* is the admin's recorded override for deleting a
     * project that still holds live app registrations; without it the server
     * refuses such a project with a 409 naming them.
     */
    mutationFn: async (vars: { projectId: number; appRegistrationsReason?: string }) => {
      await removeProjectTolerantly(vars.projectId, async () => {
        await adminDeleteProject({
          path: { project_id: vars.projectId },
          body: vars.appRegistrationsReason
            ? { app_registrations_reason: vars.appRegistrationsReason }
            : undefined,
        });
        return undefined;
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/** Mutation key of `useRemoveProject`, so lists can hide removals still in flight. */
export const REMOVE_PROJECT_MUTATION_KEY = ["removeProject"] as const;

/**
 * Owner-flow project removal. The list hides the project for as long as this
 * mutation is pending (see `useMutationState` in the home list), so the UI
 * does not sit on "Removing..." for the whole teardown and a background
 * refetch cannot bring the project back before the backend is done. The
 * project only reappears if the removal really failed
 * (see `removeProjectTolerantly`).
 */
export function useRemoveProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: REMOVE_PROJECT_MUTATION_KEY,
    mutationFn: async (vars: { projectId: number }) => {
      await removeProjectTolerantly(vars.projectId, async () => {
        const { data } = await deleteProject({ path: { project_id: vars.projectId } });
        return data;
      });
    },
    onSuccess: (_data, { projectId }) => {
      // Drop it from the cache directly: the list stops hiding the project the
      // moment this mutation settles, and the refetch below may still be
      // running (or fail) at that point.
      queryClient.setQueryData<ProjectSummary[]>(queryKeys.projects, (prev) =>
        prev ? prev.filter((p) => p.id !== projectId) : prev,
      );
    },
    onSettled: async () => {
      // Awaited on purpose: the mutation (and the pending-removal filter) only
      // ends once the list has been reconciled with the backend.
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/**
 * Re-checks every project's GitHub repo and returns the out-of-sync ones
 * (repo deleted/renamed). Invalidate projects afterwards so any statuses the
 * refresh corrected show up.
 */
export function useRefreshProjects() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await adminRefreshProjects({ query: { background: false } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

// ─── Admin activity ────────────────────────────────────────────────────────

/**
 * Server-side filtered/sorted/paginated activity feed. Every control change
 * re-runs this query with new params; `placeholderData` keeps the previous
 * page on screen while the next one loads so the table doesn't flash empty.
 */
export function useAdminActivity(params: AdminActivityParams = {}) {
  return useQuery({
    queryKey: queryKeys.adminActivity(params),
    queryFn: async () => {
      const { data } = await adminListActivity({
        query: {
          search: params.search,
          action: params.action,
          user_id: params.userId,
          sort_by: params.sortBy ?? "created_at",
          sort_dir: params.sortDir ?? "desc",
          page: params.page ?? 1,
          page_size: params.pageSize ?? 25,
        },
      });
      return data ?? null;
    },
    placeholderData: (prev) => prev,
  });
}

export function useAdminActivityAnalytics(days = 30) {
  return useQuery({
    queryKey: queryKeys.adminActivityAnalytics(days),
    queryFn: async () => {
      const { data } = await adminActivityAnalytics({ query: { days } });
      return data ?? null;
    },
  });
}

export function useAdminActivityActions() {
  return useQuery({
    queryKey: queryKeys.adminActivityActions,
    queryFn: async () => {
      const { data } = await adminActivityActions();
      return data ?? [];
    },
  });
}

// ─── Groups ──────────────────────────────────────────────────────────────

/** All groups with member counts (admin only). */
export function useGroups(enabled = true) {
  return useQuery({
    queryKey: queryKeys.groups,
    queryFn: async () => {
      const { data } = await listGroups();
      return data ?? [];
    },
    enabled,
  });
}

/** Groups the current user belongs to — powers the project-creation group
 * picker and group-name lookups on pages any authenticated user can see. */
export function useMyGroups() {
  return useQuery({
    queryKey: queryKeys.myGroups,
    queryFn: async () => {
      const { data } = await listMyGroups();
      return data ?? [];
    },
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { name: string }) => {
      const { data } = await createGroup({ body: { name: vars.name } });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

export function useUpdateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { groupId: number; name: string }) => {
      const { data } = await updateGroup({
        path: { group_id: vars.groupId },
        body: { name: vars.name },
      });
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

export function useDeleteGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { groupId: number }) => {
      await deleteGroup({ path: { group_id: vars.groupId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
      // Deleted-group projects fall back to ungrouped (owner and explicit
      // members only), which changes what the projects list should render.
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

export function useGroupMembers(groupId: number | null, enabled = true) {
  return useQuery({
    queryKey: groupId === null ? ["groups", "members", "none"] : queryKeys.groupMembers(groupId),
    queryFn: async () => {
      if (groupId === null) {
        return [];
      }
      const { data } = await listGroupMembers({ path: { group_id: groupId } });
      return data ?? [];
    },
    enabled: enabled && groupId !== null,
  });
}

export function useAddGroupMember(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await addGroupMember({ path: { group_id: groupId }, body: { user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groupMembers(groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

export function useRemoveGroupMember(groupId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { userId: string }) => {
      await removeGroupMember({ path: { group_id: groupId, user_id: vars.userId } });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.groupMembers(groupId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.groups });
    },
  });
}

// ─── App registration requests: approver queue ───────────────────────────

/**
 * The admin approver queue: every request awaiting a decision *or a retry*.
 *
 * The endpoint returns `pending` and `failed` rows together — a `failed` row is
 * one whose approval already stands but whose Graph call did not land, so it is
 * still queue work rather than a finished request.
 */
export function useAppRegistrationQueue(enabled = true) {
  return useQuery({
    queryKey: queryKeys.adminAppRegistrationRequests,
    queryFn: async () => {
      const { data } = await listPendingAppRegistrationRequests();
      return data ?? [];
    },
    enabled,
  });
}

/**
 * Approve a request and provision the app registration.
 *
 * Slow and consequential — it creates real objects in the Entra directory — so
 * callers must gate on `isPending` rather than relying on a second click being
 * harmless. Retrying a `failed` request goes through this same mutation:
 * approving again resumes provisioning instead of asking for a fresh decision.
 */
export function useApproveAppRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    // `projectId` is not sent — the route is keyed by `requestId` alone. It
    // rides in the variables so the *failure* path can invalidate the same two
    // lists the success path does: the response body is the only other place
    // the project is named, and there is no response body when the call throws.
    mutationFn: async (vars: { requestId: string; projectId: number; note: string | null }) => {
      const { data } = await approveAppRegistration({
        path: { request_id: vars.requestId },
        body: { note: vars.note },
      });
      return data ?? null;
    },
    onSuccess: (_data, vars) => {
      invalidateAppRegistrationRequest(queryClient, vars.projectId);
    },
    onError: (_error, vars) => {
      // A failed approve still changed the server: the decision is committed
      // before the first Graph call, so the row is now `failed`-with-an-error
      // rather than `pending`. Both lists show that row, so both are stale —
      // the requester's project list every bit as much as the approver's queue.
      invalidateAppRegistrationRequest(queryClient, vars.projectId);
    },
  });
}

/**
 * Refetch both views of one request: the approver's cross-project queue and the
 * requester's project-scoped list in the Resources tab.
 *
 * An approve moves the row in both lists whether it succeeds or fails — a
 * failure commits the decision and leaves the row `failed` — so they are
 * invalidated together rather than one side at a time.
 *
 * The sign-in views too, in both directions: approving an amendment that
 * *adds* permissions to a project's own sign-in registration puts it back in
 * the IT approval queue, and recording IT's consent changes the sign-in row in
 * the project's list (see `useCheckSignInApproval`, `useMarkSignInApproved`).
 */
function invalidateAppRegistrationRequest(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: number,
): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.adminAppRegistrationRequests });
  void queryClient.invalidateQueries({
    queryKey: queryKeys.projectAppRegistrationRequests(projectId),
  });
  void queryClient.invalidateQueries({ queryKey: queryKeys.signInApprovalsAll });
  void queryClient.invalidateQueries({ queryKey: queryKeys.projectSignIn(projectId) });
}

/**
 * Ask for a live app registration to be deleted.
 *
 * Creates a `delete` sibling request against the original — it does not delete
 * anything. An approver working the same queue as every other request is what
 * carries it out, which is the point: the application's deletion is
 * irreversible for whatever is still using it, so it gets a named approver
 * rather than a button.
 *
 * `confirmDisplayName` is the registration's own name typed back by the
 * requester. The server checks it too; sending it from here is what makes the
 * dialog's confirmation mean something rather than being theatre.
 */
export function useRequestAppRegistrationDecommission(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; reason: string; confirmDisplayName: string }) => {
      const { data } = await requestAppRegistrationDecommission({
        path: { project_id: projectId, request_id: vars.requestId },
        body: { reason: vars.reason, confirm_display_name: vars.confirmDisplayName },
      });
      return data ?? null;
    },
    onSuccess: () => {
      // Both sides move: the requester gains a pending row, the approver queue
      // gains a decision to make.
      invalidateAppRegistrationRequest(queryClient, projectId);
    },
  });
}

/**
 * Where the desk's records and the Entra directory disagree. Admin-only.
 *
 * Report only — there is deliberately no mutation beside it. Not fetched until
 * asked for (*enabled*) and not refetched in the background: every run reads
 * the live directory.
 */
export function useAppRegistrationReconciliation(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.appRegistrationReconciliation,
    queryFn: async () => {
      const { data } = await reconcileAppRegistrations();
      return data ?? null;
    },
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/** Reject a pending request. The note is required — the requester reads it. */
export function useRejectAppRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; note: string }) => {
      const { data } = await rejectAppRegistration({
        path: { request_id: vars.requestId },
        body: { note: vars.note },
      });
      return data ?? null;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminAppRegistrationRequests });
      if (data) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.projectAppRegistrationRequests(data.project_id),
        });
      }
    },
  });
}

// ─── Project sign-in registrations + IT approvals ─────────────────────────

/**
 * How a project signs in, and where IT's approval of each environment's own
 * sign-in registration stands.
 *
 * Only worth asking for a `dedicated` project — a `shared` one has no
 * registrations of its own — so callers gate on `ProjectSummary.sso_mode`.
 */
export function useProjectSignIn(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projectSignIn(projectId),
    queryFn: async () => {
      const { data } = await getProjectSignIn({ path: { project_id: projectId } });
      return data ?? null;
    },
    enabled,
  });
}

/**
 * How often an open IT approvals page refetches. Each answer carries freshly
 * signed admin-consent links that expire after an hour, so a page left open
 * keeps handing out working links instead of ones that bounce as "expired".
 */
const SIGN_IN_APPROVALS_REFRESH_MS = 15 * 60 * 1000;

/** The IT approval queue (admin only): sign-in registrations awaiting tenant-wide consent. */
export function useSignInApprovals(includeConsented = false) {
  return useQuery({
    queryKey: queryKeys.signInApprovals(includeConsented),
    queryFn: async () => {
      const { data } = await listSignInApprovals({
        query: { include_consented: includeConsented },
      });
      return data ?? [];
    },
    // Keep the current rows on screen while the "Show approved" toggle refetches.
    placeholderData: (prev) => prev,
    refetchInterval: SIGN_IN_APPROVALS_REFRESH_MS,
  });
}

/**
 * Ask the server to read the registration's grant from Graph — it records the
 * consent itself when the grant is there. The answer may be `unknown` (not
 * allowed to read grants), which is never an approval.
 *
 * `projectId` is not sent; it rides in the variables so `onSettled` can
 * refresh that project's views whether the call succeeded or not.
 */
export function useCheckSignInApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; projectId: number }) => {
      const { data } = await checkSignInApproval({ path: { request_id: vars.requestId } });
      return data ?? null;
    },
    onSettled: (_data, _error, vars) => {
      invalidateAppRegistrationRequest(queryClient, vars.projectId);
    },
  });
}

/**
 * Send one test approval email to the instance's configured recipients (admin
 * only). Takes a slot of the hourly email budget; `503` while approval emails
 * are off on the instance. Nothing to invalidate: it changes no listed data.
 */
export function useSendTestNotification() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await sendTestNotification();
      return data ?? null;
    },
  });
}

/**
 * Record consent by hand (admin only, audited). Changes Scaffold UI's record,
 * never Entra — the note says why the automatic detection was bypassed.
 */
export function useMarkSignInApproved() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; projectId: number; note: string }) => {
      const { data } = await markSignInApproved({
        path: { request_id: vars.requestId },
        body: { note: vars.note },
      });
      return data ?? null;
    },
    onSettled: (_data, _error, vars) => {
      invalidateAppRegistrationRequest(queryClient, vars.projectId);
    },
  });
}

// ─── Promotion requests (admin queue) ─────────────────────────────────────

/** The promotion queue (admin only): open requests oldest first, then the latest decided. */
export function useAdminPromotionRequests() {
  return useQuery({
    queryKey: queryKeys.adminPromotionRequests,
    queryFn: async () => {
      const { data } = await adminListPromotionRequests();
      return data ?? [];
    },
  });
}

/**
 * Approve a promotion request: the backend runs the same promotion as the
 * project's Promote button. A pipeline failure leaves the request `failed`
 * (approving again retries), so the queue is refreshed either way.
 */
export function useApprovePromotionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      requestId: string;
      projectId: number;
      sentryLevel: NonNullable<PromoteProjectRequest["sentry_level"]>;
      note?: string;
    }) => {
      const { data } = await adminApprovePromotionRequest({
        path: { request_id: vars.requestId },
        body: { sentry_level: vars.sentryLevel, note: vars.note || null },
      });
      return data ?? null;
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminPromotionRequests });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectEnvironments(vars.projectId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    },
  });
}

/** Reject a promotion request with a note the requester reads. Final. */
export function useRejectPromotionRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; note: string }) => {
      const { data } = await adminRejectPromotionRequest({
        path: { request_id: vars.requestId },
        body: { note: vars.note },
      });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminPromotionRequests });
    },
  });
}

// ─── Services gateway (keys, usage, budgets) ──────────────────────────────

/** A services gateway service: `serper`, `langfuse` or `llm`. */
export type SvcService = ServiceUsage["service"];

/**
 * A gateway project's dev services: keys (never the key itself), limits, this
 * month's use and its budget requests. Owner or admin only.
 */
export function useProjectServices(projectId: number, enabled = true) {
  return useQuery({
    queryKey: queryKeys.projectServices(projectId),
    queryFn: async () => {
      const { data } = await getProjectServices({ path: { project_id: projectId } });
      return data ?? null;
    },
    enabled,
  });
}

/** Ask an admin for a bigger monthly budget for one service. Sets nothing by itself. */
export function useRequestServiceBudget(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service: SvcService; budget: SetServiceBudget; reason: string }) => {
      const { data } = await requestServiceBudget({
        path: { project_id: projectId, service: vars.service },
        body: { ...vars.budget, reason: vars.reason },
      });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projectServices(projectId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminServiceBudgetRequests });
    },
  });
}

/** The budget queue (admin only): open requests oldest first, then the latest decided. */
export function useAdminServiceBudgetRequests() {
  return useQuery({
    queryKey: queryKeys.adminServiceBudgetRequests,
    queryFn: async () => {
      const { data } = await adminListServiceBudgetRequests();
      return data ?? [];
    },
  });
}

/**
 * Approve a budget request: the backend sets the budget in the key registry,
 * as asked or as the admin overrides it. A registry failure leaves it `failed`.
 */
export function useApproveServiceBudgetRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      requestId: string;
      projectId: number;
      budget?: SetServiceBudget;
      note?: string;
    }) => {
      const { data } = await adminApproveServiceBudgetRequest({
        path: { request_id: vars.requestId },
        body: { ...(vars.budget ?? {}), note: vars.note || null },
      });
      return data ?? null;
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminServiceBudgetRequests });
      invalidateServices(queryClient, vars.projectId);
    },
  });
}

/** Reject a budget request with a note the requester reads. Final. */
export function useRejectServiceBudgetRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { requestId: string; projectId: number; note: string }) => {
      const { data } = await adminRejectServiceBudgetRequest({
        path: { request_id: vars.requestId },
        body: { note: vars.note },
      });
      return data ?? null;
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminServiceBudgetRequests });
      invalidateServices(queryClient, vars.projectId);
    },
  });
}

/** Set a service's rate-limit tier (`null`: the environment default). Admin only. */
export function useSetServiceTier(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service: SvcService; tier: string | null }) => {
      await adminSetServiceTier({
        path: { project_id: projectId, service: vars.service },
        body: { tier: vars.tier },
      });
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/** Set a service's monthly budget (both empty: the environment default). Admin only. */
export function useSetServiceBudget(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service: SvcService; budget: SetServiceBudget }) => {
      await adminSetServiceBudget({
        path: { project_id: projectId, service: vars.service },
        body: vars.budget,
      });
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/** Rotate a service's key: the new one is written to the repo and dev redeploys. Admin only. */
export function useRotateServiceKey(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { service: SvcService; overlapHours: number }) => {
      const { data } = await adminRotateServiceKey({
        path: { project_id: projectId, service: vars.service },
        body: { overlap_hours: vars.overlapHours },
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/** Revoke one key now -- the emergency stop. Admin only. */
export function useRevokeServiceKey(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { keyId: string }) => {
      const { data } = await adminRevokeServiceKey({
        path: { project_id: projectId, key_id: vars.keyId },
        body: {},
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/**
 * Issue every service's key again, write them and redeploy dev. Admin only.
 * `overlapHours` 0 stops the old keys at once (a suspected leak).
 */
export function useReissueServiceKeys(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { overlapHours: number }) => {
      const { data } = await adminReissueServiceKeys({
        path: { project_id: projectId },
        body: { overlap_hours: vars.overlapHours },
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/**
 * Switch how the dev app reaches the models: its gateway key, or Foundry
 * directly (the app identity then holds Foundry User). Admin only.
 */
export function useSetLlmMode(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { mode: "gateway" | "direct" }) => {
      const { data } = await adminSetLlmMode({
        path: { project_id: projectId },
        body: { mode: vars.mode },
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/**
 * A key, limit or suspension changed: refresh the project's Services section
 * and Admin → AI services, which both show it.
 */
function invalidateServices(queryClient: QueryClient, projectId: number) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.projectServices(projectId) });
  void queryClient.invalidateQueries({ queryKey: queryKeys.adminAiServices });
}

// ─── Admin → AI services (overview, suspensions) ──────────────────────────

/**
 * Every gateway project's use and spend this month, joined to Scaffold UI's
 * own record, with the registry's totals. Admin only; `enabled` only on an
 * instance with the services gateway on (404 otherwise).
 */
export function useAdminAiServices(enabled = true) {
  return useQuery({
    queryKey: queryKeys.adminAiServices,
    queryFn: async () => {
      const { data } = await adminGetAiServices();
      return data ?? null;
    },
    enabled,
  });
}

/**
 * Suspend (a reason required) or resume one service of a gateway project:
 * its key answers 403 `suspended` within about a minute. Admin only, logged.
 */
export function useSetServiceSuspension(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      service: SvcService;
      suspended: boolean;
      reason: string | null;
    }) => {
      const { data } = await adminSetServiceSuspension({
        path: { project_id: projectId, service: vars.service },
        body: { suspended: vars.suspended, reason: vars.reason },
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/** Suspend (a reason required) or resume every service of a gateway project. Admin only. */
export function useSetProjectSuspension(projectId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { suspended: boolean; reason: string | null }) => {
      const { data } = await adminSetProjectSuspension({
        path: { project_id: projectId },
        body: { suspended: vars.suspended, reason: vars.reason },
      });
      return data ?? null;
    },
    onSettled: () => {
      invalidateServices(queryClient, projectId);
    },
  });
}

/**
 * The kill switch: suspend or resume one service for every gateway project
 * here. Suspending takes the service's name typed as `confirm`. Admin only.
 */
export function useSetServiceKillSwitch() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      service: SvcService;
      suspended: boolean;
      reason: string | null;
      confirm: string | null;
    }) => {
      const { data } = await adminSetServiceKillSwitch({
        path: { service: vars.service },
        body: { suspended: vars.suspended, reason: vars.reason, confirm: vars.confirm },
      });
      return data ?? null;
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminAiServices });
      // Every gateway project's Services section shows the switch.
      void queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "projects" && query.queryKey[2] === "services",
      });
    },
  });
}
